import type { FormEvent } from "react";

export function InvitationStartCard(props: {
  accountName: string;
  emailHint: string;
  working: boolean;
  problem: string | null;
  onSendCode: () => void;
}): React.ReactElement {
  return (
    <PageShell>
      <div style={styles.card}>
        <h1 style={styles.title}>Create your account</h1>
        <p style={styles.body}>You&apos;re joining <strong>{props.accountName}</strong>.</p>
        <AttachedEmail email={props.emailHint} />
        <p style={styles.body}>
          We&apos;ll email you a 6-digit code to make sure this invitation belongs to you.
        </p>
        {props.problem ? <p role="alert" style={styles.problem}>{props.problem}</p> : null}
        <button
          type="button"
          disabled={props.working}
          onClick={props.onSendCode}
          style={styles.primaryButton}
        >
          {props.working ? "Sending code..." : "Send code"}
        </button>
        <SecurityNote />
      </div>
    </PageShell>
  );
}

export function InvitationCodeCard(props: {
  accountName: string;
  emailHint: string;
  working: boolean;
  problem: string | null;
  deliveryUncertain: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onResend: () => void;
}): React.ReactElement {
  return (
    <PageShell>
      <div style={styles.card}>
        <h1 style={styles.title}>Check your email</h1>
        <p style={styles.body}>
          Enter the 6-digit code sent to the email attached to your invitation for <strong>{props.accountName}</strong>.
        </p>
        <AttachedEmail email={props.emailHint} />
        {props.deliveryUncertain ? (
          <p style={styles.body}>The email may take a moment. If it does not arrive, resend it below.</p>
        ) : null}
        <form onSubmit={props.onSubmit}>
          <label style={styles.fieldLabel} htmlFor="invitation-code">6-digit code</label>
          <input
            id="invitation-code"
            name="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            minLength={6}
            maxLength={6}
            required
            style={styles.input}
          />
          {props.problem ? <p role="alert" style={styles.problem}>{props.problem}</p> : null}
          <button type="submit" disabled={props.working} style={styles.primaryButton}>
            {props.working ? "Checking code..." : "Verify code"}
          </button>
        </form>
        <button
          type="button"
          disabled={props.working}
          onClick={props.onResend}
          style={styles.secondaryButton}
        >
          Send a new code
        </button>
        <SecurityNote />
      </div>
    </PageShell>
  );
}

export function PasswordCard(props: {
  accountName: string;
  email: string;
  working: boolean;
  problem: string | null;
  passwordSaved: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onRetryFinish: () => void;
}): React.ReactElement {
  return (
    <PageShell>
      <div style={styles.card}>
        <h1 style={styles.title}>Create your account</h1>
        <p style={styles.body}>You&apos;re joining <strong>{props.accountName}</strong>.</p>
        <AttachedEmail email={props.email} />
        {props.passwordSaved ? (
          <>
            <p style={styles.body}>Your password is saved.</p>
            {props.problem ? <p role="alert" style={styles.problem}>{props.problem}</p> : null}
            <button
              type="button"
              disabled={props.working}
              onClick={props.onRetryFinish}
              style={styles.primaryButton}
            >
              {props.working ? "Opening dashboard..." : "Open your dashboard"}
            </button>
          </>
        ) : (
          <form onSubmit={props.onSubmit}>
            <label style={styles.fieldLabel} htmlFor="new-password">Create password</label>
            <input
              id="new-password"
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              style={styles.input}
            />
            <p style={styles.hint}>Use at least 8 characters.</p>
            <label style={styles.fieldLabel} htmlFor="confirm-password">Confirm password</label>
            <input
              id="confirm-password"
              name="passwordAgain"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              style={styles.input}
            />
            {props.problem ? <p role="alert" style={styles.problem}>{props.problem}</p> : null}
            <button type="submit" disabled={props.working} style={styles.primaryButton}>
              {props.working ? "Creating account..." : "Create account"}
            </button>
          </form>
        )}
        <SecurityNote />
      </div>
    </PageShell>
  );
}

export function SignedInSetupConflict(props: {
  email: string;
}): React.ReactElement {
  return (
    <PageShell>
      <div style={styles.card}>
        <h1 style={styles.title}>Open this link in a private window</h1>
        <p style={styles.body}>You&apos;re signed in as <strong>{props.email}</strong>.</p>
        <p style={styles.body}>Close this tab, then open the original invitation link in a new private window.</p>
        <p style={styles.body}>If this is the invited email, contact Scale Advisors before trying again.</p>
      </div>
    </PageShell>
  );
}

export function StatusCard({ text }: { text: string }): React.ReactElement {
  return <PageShell><div style={styles.card}><p style={styles.status}>{text}</p></div></PageShell>;
}

export function InviteProblem(props: {
  title: string;
  body: string;
  actionLabel?: string;
  onAction?: () => void;
}): React.ReactElement {
  return (
    <PageShell>
      <div style={styles.card}>
        <h1 style={styles.title}>{props.title}</h1>
        <p style={styles.body}>{props.body}</p>
        {props.actionLabel && props.onAction ? (
          <button type="button" onClick={props.onAction} style={styles.primaryButton}>
            {props.actionLabel}
          </button>
        ) : null}
      </div>
    </PageShell>
  );
}

function AttachedEmail({ email }: { email: string }): React.ReactElement {
  return (
    <div style={styles.attachedEmail}>
      <span style={styles.label}>Email</span>
      <strong>{email}</strong>
      <span style={styles.attached}>Attached to this invitation</span>
    </div>
  );
}

function SecurityNote(): React.ReactElement {
  return <p style={styles.securityNote}>This personal link works once and expires. Do not share it.</p>;
}

function PageShell({ children }: { children: React.ReactNode }): React.ReactElement {
  return <main style={styles.page}>{children}</main>;
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--surface-2)", color: "var(--text)", padding: 24 },
  card: { width: "min(460px, 100%)", boxSizing: "border-box", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "var(--shadow-card)", padding: 32 },
  title: { margin: 0, fontSize: 26, lineHeight: 1.2 },
  body: { margin: "12px 0 0", color: "var(--body)", fontSize: 15, lineHeight: 1.55 },
  attachedEmail: { display: "grid", gap: 4, margin: "22px 0", padding: 14, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, overflowWrap: "anywhere" },
  label: { color: "var(--body)", fontSize: 12, fontWeight: 700 },
  attached: { color: "var(--body)", fontSize: 12 },
  fieldLabel: { display: "block", margin: "16px 0 6px", fontSize: 14, fontWeight: 700 },
  input: { width: "100%", boxSizing: "border-box", background: "var(--surface)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 8, padding: "11px 12px", font: "inherit" },
  hint: { margin: "6px 0 0", color: "var(--body)", fontSize: 12 },
  problem: { margin: "14px 0 0", color: "var(--bad)", fontSize: 14, lineHeight: 1.45 },
  primaryButton: { width: "100%", marginTop: 20, border: 0, borderRadius: 8, background: "var(--accent)", color: "var(--accent-contrast)", padding: "12px 18px", fontSize: 15, fontWeight: 750, cursor: "pointer" },
  secondaryButton: { width: "100%", marginTop: 10, border: "1px solid var(--border)", borderRadius: 8, background: "transparent", color: "var(--text)", padding: "11px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  securityNote: { margin: "14px 0 0", color: "var(--body)", fontSize: 12, lineHeight: 1.45, textAlign: "center" },
  status: { margin: 0, color: "var(--body)", textAlign: "center", fontSize: 15 },
};
